// Library Methods
import { useCallback, useMemo } from "react";

// Components
import IOLCard from "./simulation-cards/IOLCard/IOLCard";
import ProgressiveCard from "./simulation-cards/ProgressiveCard/ProgressiveCard";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import { examCardEnums, eyeTypeEnums } from "src/constants/enums";

type SimulationSliderCardType = {
  groupId: number;
  examAlgorithm: string;
};

const SimulationSliderCard = (props: SimulationSliderCardType) => {
  const { examAlgorithm, groupId } = props;

  const { selectedExamCards, actions } = useExamSliderContext();
  const { setSelectedExamCards, onRemoveCard } = actions;

  const selectedSimCard = useMemo(
    () =>
      selectedExamCards.find(
        (examCard) =>
          examCard.examFieldType === examAlgorithm &&
          examCard.groupId === groupId
      ),
    [selectedExamCards, groupId, examAlgorithm]
  );

  const updateExamCardProperty = useCallback(
    (
      propertyName: keyof SelectedExamCardType,
      value: SelectedExamCardType[keyof SelectedExamCardType]
    ) => {
      setSelectedExamCards((prev: Array<SelectedExamCardType>) => {
        // get the index of the exam to update
        const indexToUpdate = prev.findIndex(
          (exam) => exam.examFieldType === examAlgorithm
        );

        // update the correct exam card in the array
        // with the new property value
        if (indexToUpdate > -1) {
          let examToUpdate = prev[indexToUpdate];
          examToUpdate = { ...examToUpdate, [propertyName]: value };
          prev[indexToUpdate] = examToUpdate;
        }

        return [...prev];
      });
    },
    [examAlgorithm, setSelectedExamCards]
  );

  // handles changing the Both/Right/Left radio buttons on the exam card
  const onChangeEye = useCallback(
    (value: string) => {
      const { LEFT, RIGHT, BOTH } = eyeTypeEnums;
      if (value === BOTH) {
        updateExamCardProperty("eyeCheckboxes", {
          right: true,
          left: true,
        });
      } else if (value === LEFT) {
        updateExamCardProperty("eyeCheckboxes", {
          right: false,
          left: true,
        });
      } else if (value === RIGHT) {
        updateExamCardProperty("eyeCheckboxes", {
          right: true,
          left: false,
        });
      } else {
        updateExamCardProperty("eyeCheckboxes", {
          right: false,
          left: false,
        });
      }
    },
    [updateExamCardProperty]
  );

  // handles changing the Right First toggle on the exam card
  const onChangeSectionOrder = useCallback(
    (isRightFirst: boolean) => {
      updateExamCardProperty("sectionOrder", {
        right: isRightFirst ? 0 : 1,
        left: isRightFirst ? 1 : 0,
      });
    },
    [updateExamCardProperty]
  );

  const onRemoveCardWrapper = useCallback(() => {
    onRemoveCard(examAlgorithm, groupId);
  }, [examAlgorithm, groupId, onRemoveCard]);

  switch (examAlgorithm) {
    case examCardEnums.IOL_SIMULATION.algorithmPath:
      return (
        <IOLCard
          selectedSimCard={selectedSimCard}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
        />
      );
    case examCardEnums.PROGRESSIVE_SIMULATION.algorithmPath:
      return (
        <ProgressiveCard
          selectedSimCard={selectedSimCard}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    default:
      return <></>;
  }
};

export default SimulationSliderCard;
